/**
 * Every layout, as a grid of little maps to choose from.
 *
 * The Builder's gallery, and the editor's "More layouts…": the same groups in the same order, the
 * same pictures, the same names — so a layout looks and reads the same wherever somebody meets it.
 * Choosing is a radio group per shelf; what happens with the choice is the caller's.
 */
import { useMemo } from 'react';

import { layoutGallery, type GalleryOption } from '@orbit-mind/template-catalog';
import type { LayoutId } from '@orbit-mind/shared-types';

import { useCanvasFontFamily } from '../canvas/measurer';
import { layoutPreviewSrc } from './layoutPreview';

const GALLERY = layoutGallery();

export function LayoutGalleryGrid({
  chosen,
  onChoose,
  include,
}: {
  chosen: LayoutId;
  onChoose: (id: LayoutId) => void;
  /** Leaves a layout off every shelf — the whole-map ones, for a branch. */
  include?: (id: LayoutId) => boolean;
}): React.JSX.Element {
  const groups = useMemo(
    () =>
      GALLERY.map((group) => ({
        ...group,
        options: include === undefined ? group.options : group.options.filter((o) => include(o.id)),
      })).filter((group) => group.options.length > 0),
    [include],
  );

  return (
    <>
      {groups.map((group) => (
        <section key={group.key} className="mb-5">
          <h3 className="text-ink-muted mb-2 px-1 text-xs font-semibold tracking-wide uppercase">
            {group.label}
          </h3>
          <div
            role="radiogroup"
            aria-label={group.label}
            className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6"
          >
            {group.options.map((option) => (
              <LayoutCard
                key={`${group.key}-${option.id}`}
                option={option}
                checked={option.id === chosen}
                onChoose={onChoose}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function LayoutCard({
  option,
  checked,
  onChoose,
}: {
  option: GalleryOption;
  checked: boolean;
  onChoose: (id: LayoutId) => void;
}): React.JSX.Element {
  const fontFamily = useCanvasFontFamily();
  const preview = useMemo(() => layoutPreviewSrc(option.id, fontFamily), [option.id, fontFamily]);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={option.label}
      title={option.id}
      onClick={() => {
        onChoose(option.id);
      }}
      className={`rounded-panel flex flex-col overflow-hidden border text-left transition-[border-color,box-shadow] ${
        checked
          ? 'border-accent ring-accent/30 shadow-md ring-2'
          : 'border-edge hover:border-edge-strong'
      }`}
    >
      {/* A sheet of paper in both themes, as the template cards are: it is a picture of a map. */}
      <span className="block h-20 w-full" style={{ backgroundColor: '#f4f5f8' }}>
        <img src={preview} alt="" className="h-full w-full object-contain p-1.5" />
      </span>
      <span
        className={`block truncate px-2 py-1.5 text-xs font-medium ${checked ? 'text-accent' : ''}`}
      >
        {option.label}
      </span>
    </button>
  );
}
